import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Megaphone, Plus, Pin, Trash2, X, Loader2 } from "lucide-react";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "");

export default function AnnouncementsCard() {
  const { can } = useAuth();
  const canView = can("announcements.view");
  const canPost = can("announcements.post");
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [posting, setPosting] = useState(false);
  const [f, setF] = useState({ title: "", body: "", pinned: false });
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/announcements").then((r) => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoaded(true));
  useEffect(() => { if (canView) load(); }, [canView]);

  if (!canView) return null;

  const save = async () => {
    if (!f.title.trim() || !f.body.trim()) { toast.error("Title and message are required"); return; }
    setBusy(true);
    try { await api.post("/announcements", f); toast.success("Announcement posted"); setF({ title: "", body: "", pinned: false }); setPosting(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not post"); }
    finally { setBusy(false); }
  };
  const del = async (a) => { if (!window.confirm("Delete this announcement?")) return; try { await api.delete(`/announcements/${a.id}`); load(); } catch { toast.error("Delete failed"); } };

  if (!loaded) return null;
  if (items.length === 0 && !canPost) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="announcements-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold text-slate-900 flex items-center gap-2"><Megaphone className="w-4 h-4 text-amber-500" /> Company Announcements</h2>
        {canPost && <Button size="sm" className="gap-1.5 bg-amber-500 hover:bg-amber-600 text-white" onClick={() => setPosting(true)} data-testid="announcement-add-btn"><Plus className="w-4 h-4" /> Post</Button>}
      </div>
      {items.length === 0 ? (
        <div className="text-center text-sm text-slate-400 py-4 bg-slate-50/60 rounded-lg border border-dashed border-slate-200">No announcements yet.</div>
      ) : (
        <div className="space-y-2">
          {items.map((a) => (
            <div key={a.id} className={`rounded-xl border p-3 ${a.pinned ? "border-amber-200 bg-amber-50/50" : "border-slate-200"}`} data-testid={`announcement-${a.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 font-semibold text-slate-900">{a.pinned && <Pin className="w-3.5 h-3.5 text-amber-500" />}{a.title}</div>
                  <p className="text-sm text-slate-600 mt-0.5 whitespace-pre-wrap">{a.body}</p>
                  <div className="text-[11px] text-slate-400 mt-1">{a.author_name} · {fmt(a.created_at)}</div>
                </div>
                {canPost && <button onClick={() => del(a)} className="text-slate-400 hover:text-rose-600 shrink-0" title="Delete" data-testid={`announcement-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
          ))}
        </div>
      )}

      {posting && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex justify-end" onClick={() => setPosting(false)} data-testid="announcement-form">
          <div className="bg-white h-full w-full sm:w-1/2 sm:max-w-none shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <h3 className="text-xl font-bold text-slate-900">Post Announcement</h3>
              <button onClick={() => setPosting(false)} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Title</label>
                <Input value={f.title} onChange={(e) => setF((s) => ({ ...s, title: e.target.value }))} className="mt-1" data-testid="announcement-title" placeholder="e.g. Christmas party" /></div>
              <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Message</label>
                <textarea value={f.body} onChange={(e) => setF((s) => ({ ...s, body: e.target.value }))} rows={5} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100" data-testid="announcement-body" /></div>
              <div className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                <span className="text-sm text-slate-700 flex items-center gap-1.5"><Pin className="w-4 h-4 text-amber-500" /> Pin to top</span>
                <Switch checked={f.pinned} onCheckedChange={(v) => setF((s) => ({ ...s, pinned: v }))} data-testid="announcement-pin" />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPosting(false)}>Cancel</Button>
              <Button className="bg-amber-500 hover:bg-amber-600 text-white" disabled={busy} onClick={save} data-testid="announcement-save">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Post</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
